import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { catchError, map, switchMap, concatMap, withLatestFrom } from 'rxjs/operators';
import * as similarPatientsDataActions from '../actions/similar-patients.actions';
import { of } from 'rxjs/observable/of';
import { SimilarPatientService } from '@ispm-client/modules/similar-patients/services/similar-patient.service';
import {selectSimilarPatientsData} from "@ispm-client/modules/similar-patients/store/selectors/selectors";
import {buildSimilarPatientsKeys} from "@ispm-client/modules/similar-patients/store";
import {ActionTypes} from "../actions/similar-patients.actions";
import {StateTypes} from "../actions/similar-patients.actions";
import { ISimilarPatient } from '@ispm-client/modules/similar-patients/models/similar-patient.interface';

@Injectable()
export class SimilarPatientsEffects {
  @Effect()
  loadRequestEffect$: Observable<Action> =
      this.actions$
          .pipe(
              ofType<similarPatientsDataActions.FetchCurrentPatientSimilarDataAction>(
                  similarPatientsDataActions.ActionTypes.FETCH_CURRENT_PATIENT_DATA
              ),
              switchMap(action => this.similarPatientService.getSimilarPatientById(action.payload.patientId)
                  .pipe(
                      map(
                          (currentPatientData: ISimilarPatient) => {
                            return new similarPatientsDataActions.SetCurrentPatientSimilarDataAction({
                              currentPatientData
                            });
                          }
                      ),
                      catchError(error =>
                          of(new similarPatientsDataActions.SetErrorAction({ stateType: StateTypes.FETCH_CURRENT_PATIENT,  error: error.error }))
                      )
                  )
              )
          );


  @Effect()
  searchSimilarPatientsEffect$: Observable<Action> =
    this.actions$
      .pipe(
        ofType<similarPatientsDataActions.SearchSimilarPatientsDataAction>(
          similarPatientsDataActions.ActionTypes.SEARCH_SIMILAR_PATIENTS_DATA
        ),

        // (1) try to get first current data from the storage
        concatMap(action => of(action).pipe(
          withLatestFrom(this.store$.select(selectSimilarPatientsData))
        )),

        switchMap(([action, patientsData]) => {

          const similarPatientsSearchParams: any = action.payload.searchParams;

          // (2) check if data already exists in the storage for the given searchParams
          if (!!patientsData && patientsData.size > 0) {

            // build the searchParams keys in the map
            let keys = buildSimilarPatientsKeys(similarPatientsSearchParams);

            // get relevant data from the patientsData map
            let similarPatientsData: any[] = patientsData.get(keys);

            // if data exists - trigger a successful event to return the stored-data action
            if (similarPatientsData) {
              return of(new similarPatientsDataActions.SimilarPatientsActionSearchSuccess());
            }
          }

          // (3) only if data does not exist in the storage, query to fetch data
          return this.similarPatientService.searchSimilarPatients(similarPatientsSearchParams)
            .pipe(
              map(
                (similarPatientsData: any[]) => {

                  // (4) preserve the received data back to the storage
                  this.store$.dispatch({
                    type: ActionTypes.SET_SIMILAR_PATIENTS_DATA,
                    payload: {
                      similarPatientsKeys: similarPatientsSearchParams,
                      similarPatientsData: similarPatientsData
                    }
                  });

                  // trigger a successful event to return the stored-data action
                  return new similarPatientsDataActions.SimilarPatientsActionSearchSuccess();
                }

              ),

              // on fetching data error
              catchError(error =>
                of(new similarPatientsDataActions.SetErrorAction({stateType: StateTypes.SEARCH_SIMILAR_PATIENTS, error: error.error}))
              )
            )
        })
      );


  constructor(
      private actions$: Actions,
      private similarPatientService: SimilarPatientService,
      private store$: Store<any>) {

  }

}
